"use client";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getUser } from "@/services/auth";

interface TrialState {
  isTrial: boolean;
  isTrialExpired: boolean;
  trialEndsAt: Date | null;
  daysRemaining: number;
}

const DEFAULT: TrialState = {
  isTrial: false,
  isTrialExpired: false,
  trialEndsAt: null,
  daysRemaining: 0,
};

function deriveTrialState(): TrialState {
  const user = getUser();
  // subscriptionStatus drives everything — null means admin-created org (no trial)
  if (!user?.subscriptionStatus || user.subscriptionStatus === "ACTIVE") return DEFAULT;

  const isTrialExpired = user.subscriptionStatus === "EXPIRED" || user.subscriptionStatus === "CANCELLED";
  const trialEndsAt = user.trialEndsAt ? new Date(user.trialEndsAt) : null;
  const now = new Date();
  const msRemaining = trialEndsAt ? trialEndsAt.getTime() - now.getTime() : 0;
  const daysRemaining = (isTrialExpired || msRemaining <= 0) ? 0 : Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

  return { isTrial: true, isTrialExpired, trialEndsAt, daysRemaining };
}

const TrialContext = createContext<TrialState>(DEFAULT);

export function TrialProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TrialState>(DEFAULT);

  useEffect(() => {
    // Read on mount (after hydration so localStorage is available)
    setState(deriveTrialState());

    // Re-derive when auth_user changes in another tab (StorageEvent)
    // or in the same tab (custom auth_user_changed event fired by auth.ts)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setState(deriveTrialState());
    };
    const handleSameTab = () => setState(deriveTrialState());
    window.addEventListener("storage", handleStorage);
    window.addEventListener("auth_user_changed", handleSameTab);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("auth_user_changed", handleSameTab);
    };
  }, []);

  return <TrialContext.Provider value={state}>{children}</TrialContext.Provider>;
}

export function useTrial() {
  return useContext(TrialContext);
}
